(() => {
  class HxDetailTabs extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('[role="tablist"]')) return;
      const panels = [...this.querySelectorAll('[data-panel-label]')];
      if (panels.length < 2) return;
      const nav = document.createElement('div');
      nav.className = 'hx-detail-tab-nav';
      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-label', this.getAttribute('aria-label'));
      const buttons = panels.map((panel, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = panel.dataset.panelLabel;
        button.id = `${this.dataset.prefix}-tab-${index}`;
        panel.id = `${this.dataset.prefix}-panel-${index}`;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.tabIndex = 0;
        button.addEventListener('click', () => select(index));
        button.addEventListener('keydown', event => {
          const targets = { ArrowRight: (index + 1) % panels.length, ArrowLeft: (index + panels.length - 1) % panels.length, Home: 0, End: panels.length - 1 };
          if (!(event.key in targets)) return;
          event.preventDefault();
          select(targets[event.key]);
          buttons[targets[event.key]].focus();
        });
        nav.append(button);
        return button;
      });
      const select = selected => {
        panels.forEach((panel, index) => {
          panel.hidden = index !== selected;
          buttons[index].tabIndex = index === selected ? 0 : -1;
          buttons[index].setAttribute('aria-selected', String(index === selected));
        });
        const explorer = this.closest('.hx-formula-explorer');
        if (explorer) {
          const current = panels[selected];
          explorer.querySelectorAll('[data-explorer-image]').forEach(image => image.classList.toggle('is-active', image.dataset.explorerImage === current.dataset.panelImage));
          explorer.querySelector('[data-explorer-heading]').textContent = current.dataset.panelTitle;
          if (!matchMedia('(prefers-reduced-motion: reduce)').matches) current.animate([{opacity:.25,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],{duration:350,easing:'ease-out'});
        }
      };
      if (this.dataset.useAnchor) {
        this.abort = new AbortController();
        const followAnchor = () => {
          if (location.hash === `#${this.dataset.useAnchor}`) {
            const index = panels.findIndex(panel => panel.dataset.panelImage === 'usage');
            if (index >= 0) select(index);
          }
        };
        window.addEventListener('hashchange', followAnchor, {signal:this.abort.signal});
        document.addEventListener('click', event => {
          const link = event.target.closest('a');
          if (link?.hash === `#${this.dataset.useAnchor}`) {
            const index = panels.findIndex(panel => panel.dataset.panelImage === 'usage');
            if (index >= 0) select(index);
          }
        }, {signal:this.abort.signal});
      }
      this.prepend(nav);
      const initial = location.hash === `#${this.dataset.useAnchor}` ? panels.findIndex(panel => panel.dataset.panelImage === 'usage') : 0;
      select(Math.max(0, initial));
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  class HxReviewHighlights extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const events = { signal: this.controller.signal };
      this.track = this.querySelector('[data-review-track]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = this.reduced.matches;
      this.hovered = false;
      this.visible = false;
      const pause = this.querySelector('[data-review-pause]');
      const updatePause = () => {
        pause.querySelector('.hx-ui-icon path')?.setAttribute('d', this.paused ? 'm9 5 11 7-11 7Z' : 'M8 5v14M16 5v14');
        pause.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
        pause.setAttribute('aria-pressed', String(this.paused));
        this.schedule();
      };
      pause.addEventListener('click', () => { this.paused = !this.paused; updatePause(); }, events);
      this.reduced.addEventListener('change', () => { this.paused = this.reduced.matches; updatePause(); }, events);
      this.querySelectorAll('[data-review-step]').forEach(button => button.addEventListener('click', () => this.step(Number(button.dataset.reviewStep)), events));
      this.addEventListener('pointerenter', () => { this.hovered = true; this.schedule(); }, events);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, events);
      this.addEventListener('focusin', () => this.schedule(), events);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), events);
      this.track.addEventListener('pointerdown', () => { this.paused = true; updatePause(); }, events);
      this.track.addEventListener('keydown', event => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }, events);
      this.track.addEventListener('scroll', () => {
        const cards = [...this.track.children];
        this.index = cards.reduce((best, card, index) => Math.abs(card.offsetLeft - this.track.scrollLeft) < Math.abs(cards[best].offsetLeft - this.track.scrollLeft) ? index : best, 0);
      }, { passive: true, ...events });
      document.addEventListener('visibilitychange', () => this.schedule(), events);
      this.visibility = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
      this.visibility.observe(this);
      this.readReviews();
      updatePause();
    }
    readReviews() {
      if (this.items?.length) return;
      let curated;
      try { curated=JSON.parse(this.querySelector('[data-review-curation]')?.textContent || '[]'); } catch { return; }
      const items = curated.flatMap(excerpt => {
        const snapshot = String(excerpt.product_id) === this.dataset.productId ? excerpt.source : null;
        const original = snapshot;
        if (!original || original.star_rating < 4 || !excerpt.text || excerpt.text.startsWith('Translation missing')) return [];
        const text = excerpt.text.replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
        return [{text, author:original.user_name, rating:original.star_rating, verified:original.verified_review === true}];
      });
      if (!items.length) return;
      this.items = items;
      items.forEach(item => {
        const card = document.createElement('article');
        card.className = 'hx-review-highlight-card';
        const quote = document.createElement('blockquote');
        // Every excerpt retains its published source ID and verification status.
        quote.textContent = item.text.length > 220 ? `${item.text.slice(0, 220).replace(/\s+\S*$/, '')}…` : item.text;
        const meta = document.createElement('div');
        meta.className = 'hx-review-highlight-meta';
        const stars = document.createElement('span');
        stars.className = 'hx-review-highlight-stars';
        for (let index = 0; index < 5; index++) {
          const star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          star.setAttribute('viewBox', '0 0 24 24');
          star.setAttribute('aria-hidden', 'true');
          star.setAttribute('fill', index < item.rating ? 'currentColor' : 'none');
          const shape = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          shape.setAttribute('d', 'm12 2.8 2.85 5.77 6.37.93-4.61 4.49 1.09 6.35L12 17.34l-5.7 3 1.09-6.35L2.78 9.5l6.37-.93Z');
          star.append(shape); stars.append(star);
        }
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('[rating]', String(item.rating)));
        const author = document.createElement('span');
        author.className = 'hx-review-highlight-author';
        author.textContent = item.author;
        if (item.verified) {
          const badge = document.createElement('span');
          badge.className = 'hx-review-verified';
          const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
          svg.setAttribute('viewBox','0 0 20 20'); svg.setAttribute('aria-hidden','true');
          const path = document.createElementNS('http://www.w3.org/2000/svg','path');
          path.setAttribute('d','M10 1.8 12 3l2.4.3.9 2.2L17 7l-.3 2.6.3 2.5-1.7 1.6-.9 2.2L12 16l-2 1.2L8 16l-2.4-.1-.9-2.2L3 12.1l.3-2.5L3 7l1.7-1.5.9-2.2L8 3Zm-3.6 7.8 2.4 2.5 4.8-5');
          svg.append(path); badge.append(svg,document.createTextNode(this.dataset.verifiedLabel)); author.append(badge);
        }
        meta.append(stars, author);
        card.append(quote, meta);
        this.track.append(card);
      });
      this.querySelector('.hx-review-highlight-controls').hidden = items.length < 2;
      this.hidden = false;
      this.observer?.disconnect();
      this.schedule();
    }
    step(direction) {
      if (!this.items?.length) return;
      this.index = (this.index + direction + this.items.length) % this.items.length;
      this.track.scrollTo({ left: this.track.children[this.index].offsetLeft, behavior: this.reduced.matches ? 'instant' : 'smooth' });
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.items || this.items.length < 2 || this.paused || this.hovered || !this.visible || document.hidden || this.contains(document.activeElement)) return;
      this.timer = setTimeout(() => this.step(1), 5800);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
      clearTimeout(this.readTimer);
      this.observer?.disconnect();
      this.mountObserver?.disconnect();
      this.visibility?.disconnect();
    }
  }
  if (!customElements.get('hx-detail-tabs')) customElements.define('hx-detail-tabs', HxDetailTabs);
  if (!customElements.get('hx-review-highlights')) customElements.define('hx-review-highlights', HxReviewHighlights);
  const init = root => {
    root.querySelectorAll('[data-hx-information]').forEach(group => {
      if (group.dataset.ready) return;
      group.dataset.ready = 'true';
      group.querySelectorAll('details').forEach(detail => detail.addEventListener('toggle', () => {
        if (detail.open) group.querySelectorAll('details[open]').forEach(other => { if (other !== detail) other.open = false; });
      }));
    });
    root.querySelectorAll('.hx-product-tab-nav').forEach(nav => {
      if (nav.dataset.ready) return;
      nav.dataset.ready = 'true';
      const links = [...nav.querySelectorAll('a[href^="#"]')];
      const targets = links.map(link => document.getElementById(link.hash.slice(1))).filter(Boolean);
      const observer = new IntersectionObserver(entries => {
        const active = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (active) links.forEach(link => link.setAttribute('aria-current', String(link.hash === `#${active.target.id}`)));
      }, { rootMargin: '-15% 0px -35% 0px', threshold: [0, .1, .5] });
      targets.forEach(target => observer.observe(target));
      links[0]?.setAttribute('aria-current', 'true');
      document.addEventListener('shopify:section:unload', event => { if (event.target.contains(nav)) observer.disconnect(); });
    });
  };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
